import React, {useState} from 'react'
import {useMessage} from '@assistant-ui/react'

import {formatBedrockModelDisplayLabel} from '../bedrockModelLabel'
import type {Message} from '../hooks'
import {getOmicsAssistantMessage} from '../getOmicsAssistantMessage'
import {formatToolDisplayLabel} from '../toolDisplayLabels'
import {hasFilesChanged} from '../filesChanged'
import {ChatPanel} from './ChatPanel'
import {FilesChangedSection} from './FilesChangedSection'

/** Cloud units pricing: 6,000,000 units = 200 EUR => 1 EUR = 30,000 units.
 * Bedrock on-demand prices per 1M tokens (approximate, in EUR).
 */
const MODEL_PRICING: Record<string, {inputPerM: number; outputPerM: number; label: string}> = {
  opus: {inputPerM: 14.0, outputPerM: 70.0, label: 'Opus'},
  sonnet: {inputPerM: 2.8, outputPerM: 14.0, label: 'Sonnet'},
  haiku: {inputPerM: 0.75, outputPerM: 3.75, label: 'Haiku'},
  deepseek: {inputPerM: 0.28, outputPerM: 0.82, label: 'DeepSeek'},
}
const UNITS_PER_EUR = 30_000

function estimateCloudUnits(
  inputTokens: number,
  outputTokens: number,
  modelUsed?: string,
): {units: number; modelLabel: string} | null {
  if (!inputTokens && !outputTokens) return null
  const modelKey = modelUsed?.toLowerCase() ?? ''
  let pricing = MODEL_PRICING['sonnet']
  let matchedLabel = 'Unknown model'
  for (const [key, p] of Object.entries(MODEL_PRICING)) {
    if (modelKey.includes(key)) {
      pricing = p
      matchedLabel = p.label
      break
    }
  }
  const costEur =
    (inputTokens * pricing.inputPerM) / 1_000_000 + (outputTokens * pricing.outputPerM) / 1_000_000
  return {units: Math.round(costEur * UNITS_PER_EUR), modelLabel: matchedLabel}
}

function formatTokenCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

/** Knowledge base documents the turn used, with their relevance. Collapsible like Working, closed at first. */
function KbSourcesPanel({sources}: {sources: NonNullable<Message['kbSources']>}): React.ReactElement {
  const [open, setOpen] = useState(false)
  return (
    <ChatPanel
      icon='fa-book'
      title={`Knowledge Base Sources (${sources.length})`}
      open={open}
      onToggle={() => setOpen((v) => !v)}
      className='omics-kb-sources'
    >
      <ul className='omics-chat-panel__list'>
        {sources.map((src, i) => (
          <li key={i} className='omics-chat-panel__item omics-chat-panel__item--split'>
            <span className='omics-chat-panel__name'>{src.source}</span>
            {src.relevance !== undefined && (
              <span className='omics-chat-panel__aside'>{(src.relevance * 100).toFixed(0)}%</span>
            )}
          </li>
        ))}
      </ul>
    </ChatPanel>
  )
}

/** Web pages the turn used. Collapsible like Working, closed at first. */
function WebSourcesPanel({
  sources,
}: {
  sources: NonNullable<Message['internetSources']>
}): React.ReactElement {
  const [open, setOpen] = useState(false)
  return (
    <ChatPanel
      icon='fa-globe'
      title={`Web Sources (${sources.length})`}
      open={open}
      onToggle={() => setOpen((v) => !v)}
      className='omics-web-sources'
    >
      <ul className='omics-chat-panel__list'>
        {sources.map((src, i) => (
          <li key={i} className='omics-chat-panel__item'>
            <a href={src.url} target='_blank' rel='noopener noreferrer'>
              {src.title || src.url}
            </a>
          </li>
        ))}
      </ul>
    </ChatPanel>
  )
}

export function OmicsAssistantRunFooterSections({
  kbSources,
  internetSources,
  toolsUsed,
  tokenUsage,
  modelUsed,
  cloudUnits,
  agentRuntimeLabel,
  filesChanged,
  filesChangedTotal,
  filesChangedTruncated,
}: Pick<
  Message,
  | 'kbSources'
  | 'internetSources'
  | 'toolsUsed'
  | 'tokenUsage'
  | 'modelUsed'
  | 'cloudUnits'
  | 'agentRuntimeLabel'
  | 'filesChanged'
  | 'filesChangedTotal'
  | 'filesChangedTruncated'
>): React.ReactElement | null {
  const usage = tokenUsage
  const hasTokens = !!(usage && usage.total_tokens)
  const agentLine = agentRuntimeLabel?.trim() || null
  const hasAgent = !!agentLine

  const estimate =
    hasTokens && usage
      ? cloudUnits ?? estimateCloudUnits(usage.input_tokens, usage.output_tokens, modelUsed)
      : null

  const displayModelLabel =
    formatBedrockModelDisplayLabel(modelUsed) ?? estimate?.modelLabel ?? 'Model'

  const hasKb = !!(kbSources && kbSources.length)
  const hasWeb = !!(internetSources && internetSources.length)
  const hasBadges = !!(toolsUsed && toolsUsed.length)
  const hasTokenUi = hasTokens || hasAgent
  const hasFiles = hasFilesChanged({filesChanged, filesChangedTotal, filesChangedTruncated})

  if (!hasKb && !hasWeb && !hasBadges && !hasTokenUi && !hasFiles) return null

  return (
    <div className='omics-assistant-run-footer'>
      {hasFiles ? (
        <FilesChangedSection
          changes={filesChanged}
          total={filesChangedTotal}
          truncated={!!filesChangedTruncated}
        />
      ) : null}
      {hasKb ? <KbSourcesPanel sources={kbSources!} /> : null}
      {hasWeb ? <WebSourcesPanel sources={internetSources!} /> : null}
      {hasBadges ? (
        <div className='aui-tool-badges'>
          {toolsUsed!.map((tool, i) => (
            <span key={i} className='aui-tool-badges__badge'>
              {formatToolDisplayLabel(tool)}
            </span>
          ))}
        </div>
      ) : null}
      {hasTokenUi ? (
        <div className='aui-token-usage'>
          <i className='fas fa-gauge-high aui-token-usage__icon' aria-hidden />
          <span className='aui-token-usage__main'>
            {hasTokens && usage
              ? estimate
                ? `~${estimate.units.toLocaleString()} units used.`
                : `${formatTokenCount(usage.total_tokens)} tokens used`
              : agentLine}
          </span>
          {hasTokens && usage ? (
            <span className='aui-token-usage__detail' data-testid='omics-ai-chat-token-usage-detail'>
              {displayModelLabel}
              {' | '}
              {formatTokenCount(usage.input_tokens)} in
              {' + '}
              {formatTokenCount(usage.output_tokens)} out
              {usage.llm_calls > 0 &&
                ` | ${usage.llm_calls} LLM call${usage.llm_calls > 1 ? 's' : ''}`}
              {agentLine ? ` | ${agentLine}` : ''}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** Footer metadata for one assistant turn — same fields as former synthetic tool-call parts. */
export const OmicsAssistantRunFooter: React.FC = () => {
  const message = useMessage((s) => getOmicsAssistantMessage(s))
  if (!message) return null

  return (
    <OmicsAssistantRunFooterSections
      kbSources={message.kbSources}
      internetSources={message.internetSources}
      toolsUsed={message.toolsUsed}
      tokenUsage={message.tokenUsage}
      modelUsed={message.modelUsed}
      cloudUnits={message.cloudUnits}
      agentRuntimeLabel={message.agentRuntimeLabel}
      filesChanged={message.filesChanged}
      filesChangedTotal={message.filesChangedTotal}
      filesChangedTruncated={message.filesChangedTruncated}
    />
  )
}
